/* eslint-disable  @typescript-eslint/no-explicit-any */
import { getWithAuth } from '@/api/httpRequests'

import { IEPGServiceStreamIQ } from '@/shared/types/services/types/epgService/IEPGServiceStreamIQ'
import { removeFromLocalStorage } from '@/shared/localStorage/localStorageRepository'
import { EpgObject, ExtendedEpgElement } from '@/types/epg/Epg'
import Program from '@/shared/classes/epg/program/program'
import { ProgramDataStreamIQ } from '@/types/epg/EpgStreamIq'
import { StreamIQChannel } from '@/clients/streamiq/shared/classes/StreamIqChannel'
import { getEtagFromLocalStorage, setEtagToLocalStorage } from '@/shared/lib'
import { epgDataKey } from '@/shared/constants'
import ProgramStreamIQ from '@/clients/streamiq/features/EPG/programStreamIQ'
import { getBootstraps } from '@/clients/streamiq/shared/lib'

class EPGServiceStreamIQ implements IEPGServiceStreamIQ {
    mapNewDataToProgram(data: ProgramDataStreamIQ): Program {
        return new ProgramStreamIQ({
            id: data.programId || data.id,
            startMilis: data.startTime,
            endMilis: data.endTime,
            rating: data.parentalRatings?.[0]?.rating || 'NR',
            name: data.title,
            description: data.description,
            shortDescription: '',
            durationMilis: data.duration,
            poster: data.images?.length
                ? data.images?.[data.images.length - 1].url
                : 'Unknown',
            genre: data.genres || 'Unknown',
            language: data.originalLanguage,
            channelId: data.channelId,
        })
    }

    getProgramsForAllChannels = async (): Promise<any> => {
        const bootstraps = getBootstraps()

        if (!bootstraps) return

        const now = Date.now()

        const timestampMinus5Hours = now - 5 * 60 * 60 * 1000

        const timestampPlus15Hours = now + 15 * 60 * 60 * 1000

        const etag = getEtagFromLocalStorage(epgDataKey)

        let res

        if (Date.now() > Number(etag.requestTime || 0)) {
            res = await getWithAuth(
                `${bootstraps?.services?.['epg-service'].event}?startTime=${timestampMinus5Hours}&endTime=${timestampPlus15Hours}`,
                false
            )

            if (etag.eTag === res?.eTag) return

            removeFromLocalStorage(epgDataKey)
        }

        if (!res) return

        const { response, maxAge, eTag, requestTime } = res || {}

        if (!response) {
            throw new Error('No epg program are fetchet')
        }

        if (maxAge && eTag && requestTime) {
            setEtagToLocalStorage(epgDataKey, maxAge, requestTime, eTag)
        }

        const programs: Program[] = response.resources.map(
            (program: ProgramDataStreamIQ) => this.mapNewDataToProgram(program)
        )

        const channelsId = Array.from(
            new Set(programs.map((program) => program.channelId))
        )

        const epgData = channelsId.map((channelId) => ({
            channelId,
            programs: [] as Program[],
        }))

        epgData.forEach((channel) =>
            programs.forEach((program: Program) => {
                if (channel.channelId === program.channelId) {
                    channel.programs.push(program)
                }
            })
        )

        if (epgData.length) return epgData

        return
    }

    createExtendedEpg = (channels: StreamIQChannel[], apiEpg: EpgObject[]) => {
        if (!channels?.length || !Array.isArray(apiEpg)) return []
        const epgData: ExtendedEpgElement[] = channels.map(
            (channel, index) => ({
                channelId: channel.id || '',
                channelIcon: channel.icon,
                channelName: channel.name,
                channelNumber: index + 1,
                programs: [],
            })
        )
        epgData.forEach((item) =>
            apiEpg.forEach((obj) => {
                if (item.channelId === obj.channelId) {
                    item.programs = obj.programs
                }
            })
        )
        epgData.forEach((item) => this.sortProgramsByStartMilis(item.programs))

        return epgData
    }

    sortProgramsByStartMilis = (programs: Program[]) =>
        programs.sort(
            (programA, programB) => programA.startMilis - programB.startMilis
        )
}

export default new EPGServiceStreamIQ()
